import { Injectable } from '@angular/core';
import { ConfigService } from 'app/config.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { LoginTokensModel } from '../_models/login-tokens.model';
import { ApiResponseModel } from '../_models/api-response.model';
import { ChatTokenModel } from '../_models/chat-token.model';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	API_URL: string;
	API_URL_SHORT: string;
	TEAMS_API_URL: string;

	constructor(private http: HttpClient, private configService: ConfigService) {
		this.API_URL = environment.apiUrl;
		this.API_URL_SHORT = environment.apiUrlShort;
		this.TEAMS_API_URL = environment.teamsApiUrl;
	}

	// GRIP AUTH

	postLoginMailAndBagdeId(email, reg_id): Observable<any> {
		const context = 'user';
		return this.http
			.post(
				this.API_URL + 'container/verify/' + this.configService.containerId,
				{ email, reg_id, context }
			)
			.pipe(map((res: any) => res.data));
	}

	postLoginRefCode(ref_code): Observable<any> {
		const context = 'container';
		return this.http
			.post(
				this.API_URL + 'container/verify/' + this.configService.containerId,
				{ ref_code, context }
			)
			.pipe(map((res: any) => res.data));
	}

	putCommunityJoin(): Observable<any> {
		const object = {};
		return this.http
			.put(
				this.API_URL +
					'container/' +
					this.configService.containerId +
					'/thing/user_id',
				object
			)
			.pipe(map((res: any) => res.data));
	}

	postCommunityJoinCode(refcode: string, access_token): Observable<any> {
		const headers = new HttpHeaders({
			'X-Authorization': access_token
		});

		const object = {
			ref_code: refcode
		};
		return this.http
			.post(
				this.API_URL + 'container/verify/' + this.configService.containerId,
				object,
				{ headers }
			)
			.pipe(map((res: any) => res.data));
	}

	postCommunityJoin(): Observable<any> {
		const object = {};
		return this.http
			.put(
				this.API_URL +
					'container/' +
					this.configService.containerId +
					'/thing/user_id',
				object
			)
			.pipe(map((res: any) => res.data));
	}

	postLoginWithoutSSO(gripToken, registration_id): Observable<any> {
		const headers = new HttpHeaders({
			'X-Authorization': `${gripToken}`
		});

		const registration_data_present = true;
		const login_service = 'linkedin';

		if (registration_id != -1) {
			return this.http
				.post(
					this.API_URL + 'user/login',
					{
						gripToken,
						registration_id,
						login_service,
						registration_data_present
					},
					{ headers }
				)
				.pipe(map((res: any) => res.data));
		} else {
			return this.http
				.post(
					this.API_URL + 'user/login',
					{ gripToken, login_service },
					{ headers }
				)
				.pipe(map((res: any) => res.data));
		}
	}

	getMe(): Observable<any> {
		return this.http
			.get(
				this.API_URL +
				'container/' +
				this.configService.containerId +
				'/thing/user_id' +
				'?no_cache=' + Math.random()
			)
			.pipe(map((res: any) => res.data));
	}

	getChatToken(): Observable<ChatTokenModel> {
		return this.http
			.get(this.API_URL + 'chat/token')
			.pipe(map((res: any) => res.data));
	}

	postVerifyEmail(data): Observable<any[]> {
		const body = {
			...data,
			container_id: this.configService.containerId
		};
		return this.http
			.post(this.API_URL + 'user/email/verify', body)
			.pipe(map((res: any) => res.data));
	}

	postVerifyGuestEmail(data): Observable<any> {
		const body = {
			...data,
			container_id: this.configService.containerId
		};
		return this.http
			.post(this.API_URL + 'user/email/verify/guest', body)
			.pipe(map((res: any) => res.data));
	}

	postVerifyEmailTeamsInvite(data): Observable<any> {
		return this.http
			.post(this.API_URL + 'user/email/verify/teams_invite', data)
			.pipe(map((res: any) => res.data))
	}

	postVerifyBadgeId(badgeId): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'user/registration/verify', badgeId)
			.pipe(map((res: any) => res.data));
	}

	postRequestBadgeId(email): Observable<any> {
		const object = {
			email,
			container_id: this.configService.containerId
		};
		return this.http
			.post(this.API_URL + 'thing/register/remind', object)
			.pipe(map((res: any) => res.data));
	}

	postCreateAccount(body): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'user/account/create', body)
			.pipe(map((res: any) => res.data));
	}

	postEmailPasswordLogin(b64string, omitContainerId?: boolean): Observable<LoginTokensModel> {
		// setting custom headers
		const headers = new HttpHeaders({
			Authorization: `${this.configService.authorization_token}, Basic ${b64string}`
		});

		const body = omitContainerId ? {} : { container_id: this.configService.containerId };

		return this.http
			.post(
				this.API_URL + 'user/account/login',
				body,
				{ headers }
			)
			.pipe(map((res: ApiResponseModel<LoginTokensModel>) => res.data));
	}

	postEmailPasswordGuestLogin(b64string): Observable<any[]> {
		// setting custom headers
		const headers = new HttpHeaders({
			Authorization: `${this.configService.authorization_token}, Basic ${b64string}`
		});

		return this.http
			.post(this.API_URL + 'user/account/login/guest', {
				container_id: this.configService.containerId,
			}, { headers })
			.pipe(map((res: any) => res.data));
	}

	postSendPasswordReset(email): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'user/password/reset', email)
			.pipe(map((res: any) => res.data));
	}

	checkPasswordResetLink(linkRef: string): Observable<any[]> {
		return this.http
			.get(this.API_URL + 'user/password/reset/link?linkref=' + linkRef)
			.pipe(map((res: any) => res.data));
	}

	postResetPassword(linkRef: string, body: any): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'user/password/reset/link?linkref=' + linkRef, body)
			.pipe(map((res: any) => res.data));
	}

	checkPasswordQuality(password: any): Observable<any[]> {
		return this.http
			.post(this.API_URL + 'user/password/quality', password)
			.pipe(map((res: any) => res.data));
	}

	getUserPasswordStatus(): Observable<any[]> {
		return this.http
			.get(this.API_URL + 'user/settings/password')
			.pipe(map((res: any) => res.data));
	}

	putEditPassword(password): Observable<any[]> {
		return this.http
			.put(this.API_URL + 'user/password/save', password)
			.pipe(map((res: any) => res.data));
	}

	// TEAMS JOIN //

	getTeamsInviteStatus(linkRef, email): Observable<any> {
		let ieParam = '';
		if (window.navigator.userAgent.match(/(MSIE|Trident|Edge)/)) {
			ieParam = '&redirect=0';
		}

		return this.http
			.get(
				this.TEAMS_API_URL +
					'user/account/invite/link?linkref=' +
					linkRef +
					'&email=' +
					encodeURIComponent(email) +
					ieParam
			)
			.pipe(map((res: any) => res.data));
	}

	postCreateTeamsAccount(body): Observable<any[]> {
		return this.http
			.post(this.TEAMS_API_URL + 'user/account/create', body)
			.pipe(map((res: any) => res.data));
	}

	// SSO EXTERNAL //

	postGenericSSO(username, password, containerId): Observable<any> {
		const object = {
			protocol: this.configService.sso_protocol,
			idp_name: this.configService.sso_idp_name,
			sso_data: {
				username,
				password,
				container_id: undefined
			}
		};
		if (
			object.protocol === 'proprietary' ||
			object.protocol === 'proprietary_two_step'
		) {
			object.sso_data.container_id = containerId;
		}
		return this.http
			.post(this.API_URL + 'user/ssologin', object)
			.pipe(map((res: any) => res.data));
	}

	postDimedisSSO(ticket, containerId): Observable<any> {
		const object = {
			protocol: 'proprietary',
			idp_name: 'dimedis-passthru',
			sso_data: {
				ticket,
				container_id: containerId
			}
		};
		return this.http
			.post(this.API_URL + 'user/ssologin', object)
			.pipe(map((res: any) => res.data));
	}

	postFreemanSSO(id, eventId, containerId): Observable<any> {
		const object = {
			protocol: 'proprietary',
			idp_name: 'freeman-passthru',
			sso_data: {
				event_id: eventId,
				id,
				container_id: containerId
			}
		};
		return this.http
			.post(this.API_URL + 'user/ssologin', object)
			.pipe(map((res: any) => res.data));
	}

	postEventbaseSSO(ticket, containerId): Observable<any> {
		const object = {
			protocol: 'proprietary',
			idp_name: 'eventbase-passthru',
			sso_data: {
				ticket,
				container_id: containerId
			}
		};
		return this.http
			.post(this.API_URL + 'user/ssologin', object)
			.pipe(map((res: any) => res.data));
	}

	postSapSSO(email, UID, UIDSignature, signatureTimestamp, containerId): Observable<any> {
		const object = {
			protocol: 'proprietary',
			idp_name: 'sap-passthru',
			sso_data: {
				email,
				UID,
				UIDSignature,
				signatureTimestamp,
				container_id: containerId
			}
		};
		return this.http
			.post(this.API_URL + 'user/ssologin', object)
			.pipe(map((res: any) => res.data));
	}

  postCircdataSSO(token, containerId): Observable<any> {
		const object = {
			protocol: 'proprietary',
			idp_name: 'circdata-passthru',
			sso_data: {
				token,
				container_id: containerId
			}
		};
		return this.http
			.post(this.API_URL + 'user/ssologin', object)
			.pipe(map((res: any) => res.data));
	}

	getSamlMetadata(): Observable<any> {
		return this.http
			.get(this.API_URL + `user/saml2/login?container_id=${this.configService.containerId}`)
			.pipe(map((res: any) => res.data));
	}

	postSamlSso(userToken): Observable<any> {
		const body = {
			token: userToken
		};
		return this.http
			.post(this.API_URL + 'user/account/ssologin', body)
			.pipe(map((res: any) => res.data));
	}

	postOpenIdConnectSSO(authCode, codeVerifier, state ): Observable<any> {
		const body = {
			protocol: this.configService.sso_protocol,
			idp_name: this.configService.sso_idp_name,
			sso_data: {
				authorization_code: authCode,
				container_id: this.configService.containerId,
				container_name: this.configService.configName
			}
		};

		// if PKCE is enabled, add to body
		if (this.configService.sso_pkce && codeVerifier) {
			body.sso_data['initiator'] = this.configService.sso_initiator;
			body.sso_data['code_verifier'] = codeVerifier;
			if (this.configService.sso_state) {
				body.sso_data['state'] = state;
			}
		}
		return this.http
			.post(this.API_URL + 'user/ssologin', body)
			.pipe(map((res: any) => res.data));
	}

	postNoRequestSSO(token: string): Observable<any> {
		const body = {
			protocol: this.configService.sso_protocol,
			idp_name: this.configService.sso_idp_name,
			sso_data: {
				initiator: 'client',
				token
			}
		};
		return this.http
			.post(this.API_URL + 'user/ssologin', body)
			.pipe(map((res: any) => res.data));
	}

	// SOCIAL MEDIA //

	postLoginLinkedInAuth(
		application_id: any,
		auth_code: string,
		redirect_uri: string
	): Observable<any> {
		return this.http
			.post(this.API_URL_SHORT + 'linkedin/access_token', {
				application_id,
				auth_code,
				redirect_uri
			})
			.pipe(map((res: any) => res.data));
	}

	postLoginLinkedIn(
		linkedInToken,
		linkedin_profile,
		user_registration_id
	): Observable<any> {
		const headers = new HttpHeaders({
			'X-Linked-In-Token': `${linkedInToken}`
		});

		const registration_data_present = true;
		const login_service = 'linkedin';

		const object: any = {};
		object.login_service = login_service;

		if (linkedin_profile != null && Object.keys(linkedin_profile).length != 2) {
			object.linkedin_profile = linkedin_profile;
		}

		if (user_registration_id != -1) {
			object.registration_id = user_registration_id;
			object.registration_data_present = registration_data_present;
		}

		return this.http
			.post(this.API_URL + 'user/login?linkedin_version=2', object, { headers })
			.pipe(map((res: any) => res.data));
	}

	getLinkedInProfileData(accessToken): Observable<any> {
		const headers = new HttpHeaders({
			'x-linked-in-token': accessToken
		});

		return this.http
			.get(this.API_URL + 'user/profile/linkedin?linkedin_version=2', {
				headers
			})
			.pipe(map((res: any) => res.data));
	}

	deleteAccount(): Observable<any[]> {
		return this.http
			.delete(this.API_URL + 'thing/user_id')
			.pipe(map((res: any) => res.data));
	}

	createAnonymousUser(): Observable<any> {
		return this.http
			.post(this.API_URL + 'user/login/anonymous', {
				container_id: this.configService.containerId
			})
			.pipe(map((res: any) => res.data));
	}

	userLogout(): Observable<any> {
		return this.http
			.post(this.API_URL + 'user/account/logout', {
				container_id: this.configService.containerId
			});
	}

	userSSOLogout(): Observable<any> {
		return this.http
			.post(this.API_URL + 'user/account/ssologout', {
				container_id: this.configService.containerId
			});
	}

	checkOtp(otpToken: string): Observable<any> {
		return this.http
			.post(`${this.API_URL}user/beta/${otpToken}`, {})
			.pipe(map((res: any) => res.data));
	}

	// get otp token for ai assistant link
	getAiAssistantOtp(): Observable<any> {
		return this.http
			.get(`${this.API_URL}user/otp`)
			.pipe(map((res: any) => res.data));
	}

	/**
	 * Generates a PKCE code verifier and challenge, stores challenge in localStorage, and returns verifier, challenge, method, and state.
	 * @returns An object containing code_verifier, code_challenge, code_challenge_method, and state.
	 */
	async generateAndStorePkceVerifierAndChallenge(): Promise<{ code_verifier: string; code_challenge: string; code_challenge_method: string; state: string; }> {
		// Helper to generate a random ArrayBuffer
		function generateRandom(length: number): ArrayBuffer {
			return window.crypto.getRandomValues(new Uint8Array(length)).buffer;
		}

		// Helper to base64-url encode an ArrayBuffer
		function base64UrlEncode(arrayBuffer: ArrayBuffer): string {
			return btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)))
				.replace(/\+/g, '-')
				.replace(/\//g, '_')
				.replace(/=+$/, '');
		}

		// Helper to sha256 hash a string
		async function sha256(plain: string): Promise<ArrayBuffer> {
			const encoder = new TextEncoder();
			const data = encoder.encode(plain);
			return await window.crypto.subtle.digest('SHA-256', data);
		}

		const codeVerifier = base64UrlEncode(generateRandom(64));
		let state = '';
		if (this.configService.sso_state) {
			state = base64UrlEncode(generateRandom(32));
		}

		if (localStorage && typeof localStorage.setItem === 'function') {
			localStorage.setItem('pkce:' + state, codeVerifier);
		}

		let codeChallenge = base64UrlEncode(await sha256(codeVerifier));

		return { code_verifier: codeVerifier, code_challenge: codeChallenge, code_challenge_method: 'S256', state };
	}

	/**
	 * Retrieves the PKCE code verifier from localStorage using the state value as part of the key.
	 * @param state The state string used as part of the storage key.
	 * @returns The code verifier string, or null if not found.
	 */
	getPkceCodeVerifierFromState(state: string): string | null {
		if (!state) {
			state = '';
		}
		const key = 'pkce:' + state;
		return localStorage.getItem(key);
	}

	/**
	 * Removes the PKCE code verifier from localStorage using the state value as part of the key.
	 * @param state The state string used as part of the storage key.
	 */
	removePkceCodeVerifierFromState(state: string): void {
		if (!state) {
			state = '';
		}
		const key = 'pkce:' + state;
		localStorage.removeItem(key);
	}
	
}
